import { useState, useEffect } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { 
  ClipboardList, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye,
  Globe,
  FileText,
  Image,
  Video,
  Loader2,
  CheckCircle2,
  XCircle,
  Calendar,
  Save
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const AdminContentPage = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('pages');
  const [showDialog, setShowDialog] = useState(false);
  const [editingContent, setEditingContent] = useState(null);

  // Sample content data
  const sampleContent = {
    pages: [
      {
        id: 'page_001',
        title: 'Inicio',
        slug: '/',
        status: 'published',
        lastModified: '2026-03-15',
        type: 'page'
      },
      {
        id: 'page_002',
        title: 'Nosotros',
        slug: '/nosotros',
        status: 'published',
        lastModified: '2026-04-09',
        type: 'page'
      },
      {
        id: 'page_003',
        title: 'Servicios',
        slug: '/servicios',
        status: 'published',
        lastModified: '2026-03-10',
        type: 'page'
      },
      {
        id: 'page_004',
        title: 'Proyectos',
        slug: '/proyectos',
        status: 'published',
        lastModified: '2026-03-12',
        type: 'page'
      },
      {
        id: 'page_005',
        title: 'Contacto',
        slug: '/contacto',
        status: 'published',
        lastModified: '2026-02-28',
        type: 'page'
      }
    ],
    faqs: [
      {
        id: 'faq_001',
        question: '¿Cuál es la inversión mínima?',
        answer: 'La inversión mínima varía según el proyecto, generalmente desde $250,000 MXN.',
        category: 'inversión',
        status: 'published',
        order: 1
      },
      {
        id: 'faq_002',
        question: '¿Cómo se distribuyen los rendimientos?',
        answer: 'Los rendimientos se distribuyen trimestralmente a los inversionistas aprobados.',
        category: 'rendimientos',
        status: 'published',
        order: 2
      },
      {
        id: 'faq_003',
        question: '¿Qué documentos necesito para invertir?',
        answer: 'Se requiere identificación oficial, comprobante de domicilio y documentación fiscal.',
        category: 'kyc',
        status: 'published',
        order: 3
      },
      {
        id: 'faq_004',
        question: '¿Cuánto tiempo dura una inversión típica?',
        answer: 'Los plazos varían entre 24 y 60 meses según el tipo de proyecto.',
        category: 'inversión',
        status: 'published',
        order: 4
      }
    ],
    announcements: [
      {
        id: 'ann_001',
        title: 'Nueva Oportunidad: Torre Reforma Elite',
        content: 'Anunciamos la apertura de una nueva oportunidad de inversión en el corredor Reforma.',
        status: 'published',
        publishDate: '2026-03-01',
        expiryDate: '2026-04-30'
      },
      {
        id: 'ann_002',
        title: 'Reporte Q1 2026 Disponible',
        content: 'Ya está disponible el reporte financiero del primer trimestre 2026.',
        status: 'published',
        publishDate: '2026-04-05',
        expiryDate: null
      }
    ],
    legal: [
      {
        id: 'legal_001',
        title: 'Términos y Condiciones',
        slug: '/terminos',
        status: 'published',
        lastModified: '2026-01-15'
      },
      {
        id: 'legal_002',
        title: 'Aviso de Privacidad',
        slug: '/privacidad',
        status: 'published',
        lastModified: '2026-01-15'
      },
      {
        id: 'legal_003',
        title: 'Política de Riesgos',
        slug: '/riesgos',
        status: 'published',
        lastModified: '2026-02-20'
      }
    ]
  };

  useEffect(() => {
    setTimeout(() => {
      setContent(sampleContent);
      setLoading(false);
    }, 500);
  }, []);

  const getStatusBadge = (status) => {
    const colors = {
      published: 'bg-green-500/10 text-green-500',
      draft: 'bg-yellow-500/10 text-yellow-500',
      archived: 'bg-gray-500/10 text-gray-500'
    };
    const labels = {
      published: 'Publicado',
      draft: 'Borrador',
      archived: 'Archivado'
    };
    return (
      <Badge className={`${colors[status] || 'bg-muted'} border-0`}>
        {labels[status] || status}
      </Badge>
    );
  };

  const handleEdit = (item) => {
    setEditingContent(item);
    setShowDialog(true);
  };

  const handleDelete = (id, type) => {
    if (confirm('¿Estás seguro de eliminar este contenido?')) {
      // In real implementation, delete from backend
      // removed debug log
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-light tracking-tight">Gestión de Contenido</h1>
          <p className="text-muted-foreground mt-1">
            Administra el contenido del sitio web
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="card-premium p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
              <Globe className="h-5 w-5 text-blue-500" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-light">{content.pages?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Páginas</p>
            </div>
          </div>
        </div>
        <div className="card-premium p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-purple-500" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-light">{content.faqs?.length || 0}</p>
              <p className="text-xs text-muted-foreground">FAQs</p>
            </div>
          </div>
        </div>
        <div className="card-premium p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-green-500" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-light">{content.announcements?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Anuncios</p>
            </div>
          </div>
        </div>
        <div className="card-premium p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-orange-500/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-orange-500" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-light">{content.legal?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Documentos Legales</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="pages">Páginas</TabsTrigger>
          <TabsTrigger value="faqs">FAQs</TabsTrigger>
          <TabsTrigger value="announcements">Anuncios</TabsTrigger>
          <TabsTrigger value="legal">Legal</TabsTrigger>
        </TabsList>

        {/* Pages Tab */}
        <TabsContent value="pages">
          <div className="card-premium divide-y divide-border">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              content.pages?.map((page) => (
                <div key={page.id} className="p-4 flex items-center justify-between hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                      <Globe className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-medium">{page.title}</p>
                      <p className="text-sm text-muted-foreground">{page.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getStatusBadge(page.status)}
                    <span className="text-xs text-muted-foreground">{page.lastModified}</span>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(page)}>
                        <Edit className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Eye className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* FAQs Tab */}
        <TabsContent value="faqs">
          <div className="flex justify-end mb-4">
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Nueva FAQ
            </Button>
          </div>
          <div className="card-premium divide-y divide-border">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              content.faqs?.map((faq) => (
                <div key={faq.id} className="p-4 hover:bg-muted/30 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="font-medium">{faq.question}</p>
                        <Badge variant="outline" className="text-xs">{faq.category}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{faq.answer}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(faq.status)}
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(faq)}>
                        <Edit className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(faq.id, 'faq')}>
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Announcements Tab */}
        <TabsContent value="announcements">
          <div className="flex justify-end mb-4">
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Nuevo Anuncio
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {loading ? (
              <div className="col-span-full flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              content.announcements?.map((announcement) => (
                <div key={announcement.id} className="card-premium p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {getStatusBadge(announcement.status)}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(announcement)}>
                        <Edit className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                  <h3 className="font-medium mb-2">{announcement.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{announcement.content}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Publicado: {announcement.publishDate}</span>
                    {announcement.expiryDate && <span>Expira: {announcement.expiryDate}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Legal Tab */}
        <TabsContent value="legal">
          <div className="card-premium divide-y divide-border">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              content.legal?.map((doc) => (
                <div key={doc.id} className="p-4 flex items-center justify-between hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                      <FileText className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-medium">{doc.title}</p>
                      <p className="text-sm text-muted-foreground">{doc.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getStatusBadge(doc.status)}
                    <span className="text-xs text-muted-foreground">{doc.lastModified}</span>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(doc)}>
                        <Edit className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Eye className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Editar Contenido
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Título</Label>
              <Input
                value={editingContent?.title || editingContent?.question || ''}
                placeholder="Título del contenido"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Contenido</Label>
              <Textarea
                value={editingContent?.content || editingContent?.answer || ''}
                placeholder="Contenido..."
                rows={6}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select defaultValue={editingContent?.status || 'published'}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Publicado</SelectItem>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="archived">Archivado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={() => setShowDialog(false)}>
              <Save className="h-4 w-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminContentPage;
